import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Dropping a screenshot should work anywhere over the support panel, but the pending-file list
 * lives inside the composer. The panel owns a slot; the composer on screen registers itself into
 * it, and the drop handler hands files to whatever is registered. No composer (ticket list, or a
 * closed ticket) means no slot and nothing to drop onto.
 */
export interface AttachTarget {
  addFiles: (files: File[]) => void;
  openPicker: () => void;
}

export type AttachSlot = React.MutableRefObject<AttachTarget | null>;

export const AttachSlotContext = createContext<AttachSlot>({ current: null });

export function useAttachSlot(): AttachSlot {
  return useContext(AttachSlotContext);
}

/** Claim the panel's attach slot for as long as this composer is mounted. */
export function useRegisterAttachTarget(target: AttachTarget): void {
  const slot = useAttachSlot();
  const latest = useRef(target);
  latest.current = target;

  useEffect(() => {
    // Indirection through the ref keeps this entry stable, so the effect runs once per mount
    // instead of on every keystroke that rebuilds `addFiles`.
    const entry: AttachTarget = {
      addFiles: (files) => latest.current.addFiles(files),
      openPicker: () => latest.current.openPicker(),
    };
    slot.current = entry;
    return () => {
      if (slot.current === entry) slot.current = null;
    };
  }, [slot]);
}

const carriesFiles = (e: React.DragEvent): boolean =>
  Array.from(e.dataTransfer?.types ?? []).includes('Files');

/**
 * Drag-and-drop for one element. `dragging` is true while a file is held over it, which is what
 * the overlay renders on. Enter/leave are counted because they also fire when the pointer crosses
 * a child element, and a naive boolean flickers the overlay off mid-drag.
 */
export function useFileDrop(onFiles: (files: File[]) => void, enabled: boolean) {
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);

  const reset = useCallback(() => {
    depth.current = 0;
    setDragging(false);
  }, []);

  useEffect(() => {
    if (!enabled) reset();
  }, [enabled, reset]);

  const dropProps = {
    onDragEnter: (e: React.DragEvent) => {
      if (!enabled || !carriesFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      setDragging(true);
    },
    onDragOver: (e: React.DragEvent) => {
      if (!enabled || !carriesFiles(e)) return;
      // Without preventDefault here the browser refuses the drop and opens the file instead.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      if (depth.current === 0) {
        depth.current = 1;
        setDragging(true);
      }
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!enabled || !carriesFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (e: React.DragEvent) => {
      if (!enabled || !carriesFiles(e)) return;
      e.preventDefault();
      reset();
      onFiles(Array.from(e.dataTransfer?.files ?? []));
    },
  };

  return { dragging, dropProps };
}

/**
 * While the panel is open, a file dropped just outside it would make the browser navigate to that
 * file and throw away the page. Swallow those drops at the window.
 */
export function usePreventWindowFileDrop(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const swallow = (e: DragEvent) => {
      if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, [enabled]);
}
